import { Injectable, inject } from '@angular/core';
import {
  CoreRouterActionNavigateForward,
  CoreRouterActionNavigateToPageModule,
  CoreRouterActions,
  CoreRouterNavigationIntent,
  selectorCoreInstanceInfo,
  selectorCoreNavigateToSingleResourceHref,
  selectorCoreRootPageModuleID
} from '@bepatient/core';
import { ToastController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import {
  catchError,
  delay,
  map,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { BodySchemaApiService } from './body-schema-api.service';
import {
  BodySchemaActionLoadBodySchemaFailed,
  BodySchemaActionLoadBodySchemaSuccess,
  BodySchemaActions,
  BodySchemaActionSaveBodySchemaFailed,
  BodySchemaActionSaveBodySchemaSuccess,
  BodySchemaActionTypes
} from './body-schema.actions';
import {
  selectorBodySchemaDataForMOBI,
  selectorBodySchemaMouthZoneSelected,
  selectorBodySchemaPelvicZoneSelected
} from './body-schema.reducer';

@Injectable()
export class BodySchemaEffects {
  private store$ = inject<Store<any>>(Store);
  private action$ = inject(Actions);
  private translate = inject(TranslateService);
  private toastController = inject(ToastController);
  private bodySchemaApiService = inject(BodySchemaApiService);

  setPainZonesCompleted$: Observable<CoreRouterActions> = createEffect(() =>
    this.action$.pipe(
      ofType(BodySchemaActionTypes.SET_PAIN_ZONES_COMPLETED),
      withLatestFrom(
        this.store$.select(selectorBodySchemaMouthZoneSelected),
        this.store$.select(selectorBodySchemaPelvicZoneSelected)
      ),
      map(([, mouthSelected, pelvicSelected]) => {
        if (mouthSelected) {
          return new CoreRouterActionNavigateForward({
            url: 'body-schema-mouth'
          });
        } else if (pelvicSelected) {
          return new CoreRouterActionNavigateForward({
            url: 'body-schema-pelvic'
          });
        } else {
          return new CoreRouterActionNavigateForward({
            url: 'body-schema-details'
          });
        }
      })
    )
  );

  setMouthPainLocation$: Observable<CoreRouterActions> = createEffect(() =>
    this.action$.pipe(
      ofType(BodySchemaActionTypes.SET_MOUTH_PAIN_LOCATION),
      withLatestFrom(this.store$.select(selectorBodySchemaPelvicZoneSelected)),
      map(
        ([, pelvicSelected]) =>
          new CoreRouterActionNavigateForward({
            url: pelvicSelected ? 'body-schema-pelvic' : 'body-schema-details'
          })
      )
    )
  );

  setPelvicPainLocation$: Observable<CoreRouterActions> = createEffect(() =>
    this.action$.pipe(
      ofType(BodySchemaActionTypes.SET_PELVIC_PAIN_LOCATION),
      map(
        () =>
          new CoreRouterActionNavigateForward({
            url: 'body-schema-details'
          })
      )
    )
  );

  loadBodySchema$: Observable<BodySchemaActions> = createEffect(() =>
    this.action$.pipe(
      ofType(BodySchemaActionTypes.LOAD_BODY_SCHEMA),
      withLatestFrom(
        this.store$.select(selectorCoreInstanceInfo),
        this.store$.select(selectorCoreNavigateToSingleResourceHref)
      ),
      switchMap(([, instanceInfo, href]) =>
        this.bodySchemaApiService.loadBodySchema(href).pipe(
          map(
            data =>
              new BodySchemaActionLoadBodySchemaSuccess({
                ...data,
                color: data.color || instanceInfo.appColor
              })
          ),
          catchError(() => of(new BodySchemaActionLoadBodySchemaFailed()))
        )
      )
    )
  );

  saveBodySchema$: Observable<BodySchemaActions> = createEffect(() =>
    this.action$.pipe(
      ofType(BodySchemaActionTypes.SAVE_BODY_SCHEMA),
      withLatestFrom(
        this.store$.select(selectorCoreNavigateToSingleResourceHref),
        this.store$.select(selectorBodySchemaDataForMOBI)
      ),
      switchMap(([, href, data]) =>
        this.bodySchemaApiService.saveBodySchema(href, data).pipe(
          map(() => new BodySchemaActionSaveBodySchemaSuccess()),
          catchError(() => of(new BodySchemaActionSaveBodySchemaFailed()))
        )
      )
    )
  );

  saveBodySchemaSuccess$: Observable<CoreRouterActions> = createEffect(() =>
    this.action$.pipe(
      ofType(BodySchemaActionTypes.SAVE_BODY_SCHEMA_SUCCESS),
      withLatestFrom(this.store$.select(selectorCoreRootPageModuleID)),
      map(
        ([, moduleID]) =>
          new CoreRouterActionNavigateToPageModule({
            id: moduleID,
            asRoot: true,
            intent: CoreRouterNavigationIntent.BACK
          })
      )
    )
  );

  errorToast = createEffect(
    () =>
      this.action$.pipe(
        ofType(
          BodySchemaActionTypes.LOAD_BODY_SCHEMA_FAILED,
          BodySchemaActionTypes.SAVE_BODY_SCHEMA_FAILED
        ),
        tap(() => this.toast(this.translate.instant('SAVE_FAILED')))
      ),
    { dispatch: false }
  );

  successToast = createEffect(
    () =>
      this.action$.pipe(
        ofType(BodySchemaActionTypes.SAVE_BODY_SCHEMA_SUCCESS),
        delay(500),
        tap(() => this.toast(this.translate.instant('SAVE_SUCCESS')))
      ),
    { dispatch: false }
  );

  private toast(message: string): void {
    this.toastController
      .create({
        message: message,
        position: 'top',
        duration: 2000,
        cssClass: 'bepatient_dynamic_form_save_toast'
      })
      .then(toast => toast.present());
  }
}
